import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BreadcrumbModel } from '.';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class EllipsesService {
  constructor(@Inject(PLATFORM_ID) private platformId: object) {}

  generateEllipses(breadcrumbs: BreadcrumbModel[]) {
    const breadcrumbElement = this.getFirstElementBy('required-to-do-work-before-init');

    let firstRemainingBreadcrumb: BreadcrumbModel;
    let safeBreadcrumbs = breadcrumbs;
    if (this.isHeightChanged(breadcrumbs, breadcrumbElement, breadcrumbs.length)) {
      const currentHeightIndex = this.getIncreasedHeightIndex(breadcrumbs, breadcrumbElement);

      const newBreadcrumbs = Object.assign([] as BreadcrumbModel[], breadcrumbs);
      safeBreadcrumbs = newBreadcrumbs.splice(0, currentHeightIndex) as BreadcrumbModel[];

      const remainingBreadcrumbs = newBreadcrumbs;
      firstRemainingBreadcrumb = remainingBreadcrumbs[0];
    }

    if (firstRemainingBreadcrumb) {
      const wordBreadcrumbs = this.buildBreadcrumbsFromWords(firstRemainingBreadcrumb);

      const combinedBreadcrumbLists = safeBreadcrumbs.concat(wordBreadcrumbs);

      const wordIndexOfHeightIncrease = this.getIncreasedHeightIndex(combinedBreadcrumbLists, breadcrumbElement);
      const newWordIndexOfHeightIncrease = this.minus(wordIndexOfHeightIncrease, safeBreadcrumbs.length);

      const wordBreadcrumbsToBeSliced = Object.assign([], wordBreadcrumbs);

      const safeWordBreadcrumbs = wordBreadcrumbsToBeSliced.splice(0, newWordIndexOfHeightIncrease);

      const breadcrumbNameChars = safeWordBreadcrumbs.map((b: BreadcrumbModel) => b.name);
      const words: string[] = this.createListOfWordsFrom(breadcrumbNameChars);

      const newWordBreadcrumbs: BreadcrumbModel[] = words.map((w) => {
        return {
          name: w,
          routerLink: '',
        };
      });

      let lastUrlPart = '';
      const newWordBreadcrumbsLength = newWordBreadcrumbs.length;
      newWordBreadcrumbs.forEach((crumb, index) => {
        lastUrlPart += `${crumb.name} `;

        const to = index;
        const comparisonOne = newWordBreadcrumbsLength - 1;
        if (this.isEqual(to, comparisonOne)) {
          const lessThan = newWordBreadcrumbsLength;
          const comparisonTwo = wordBreadcrumbs.length;
          if (this.isFirstNumber(lessThan, comparisonTwo)) {
            lastUrlPart = this.appendEllipsesTo(lastUrlPart);
          }
        }
      });

      const routerLink = '';
      const name = lastUrlPart;
      safeBreadcrumbs.push(this.createBreadcrumb([{ name, routerLink }]));
    }

    const templateElement = this.getFirstElementBy('required-to-do-work-before-init-breadcrumb-template');
    const arrowElement = this.getFirstElementBy('required-to-do-work-before-init-icon');
    this.removeElementsFromDom([breadcrumbElement, templateElement, arrowElement]);

    return safeBreadcrumbs;
  }

  createBreadCrumbElementsForHeightReference() {
    if (isPlatformBrowser(this.platformId)) {
      const createRequiredBeforeInit = document.createElement('div');
      createRequiredBeforeInit.classList.add('required-to-do-work-before-init');

      const faHomeIcon = document.createElement('i');
      faHomeIcon.classList.add('fas');
      faHomeIcon.classList.add('fa-home');
      faHomeIcon.classList.add('breadcrumb__container__icon');

      createRequiredBeforeInit.appendChild(faHomeIcon);

      const createBreadCrumbTemplate = document.createElement('div');
      createBreadCrumbTemplate.classList.add('required-to-do-work-before-init-breadcrumb-template');

      const faAngleLeft = document.createElement('i');
      faAngleLeft.classList.add('required-to-do-work-before-init-icon');
      faAngleLeft.classList.add('fas');
      faAngleLeft.classList.add('fa-angle-left');

      const selectBreadCrumbNav = document.getElementsByClassName('breadcrumb')[0] as HTMLElement;
      document.body.insertBefore(createRequiredBeforeInit, selectBreadCrumbNav.nextSibling);

      const requiredBI = document.getElementsByClassName('required-to-do-work-before-init')[0] as HTMLElement;
      document.body.insertBefore(createBreadCrumbTemplate, requiredBI.nextSibling);

      const bcTempl = document.getElementsByClassName(
        'required-to-do-work-before-init-breadcrumb-template'
      )[0] as HTMLElement;
      document.body.insertBefore(faAngleLeft, bcTempl.nextSibling);
    }
  }

  createListOfWordsFrom(chars: string[]) {
    const words: string[] = [];
    let word = '';
    chars.forEach((name, i) => {
      if (name === ' ') {
        words.push(word.trim());
        word = '';
      }
      word += name.trim();

      if (i === chars.length - 1) {
        words.push(word);
      }
    });
    return words;
  }

  appendEllipsesTo(word) {
    const lastUrlPartLength = word.length;
    let result = word.slice(0, lastUrlPartLength).trim();

    return (result += '...');
  }

  isEqual(to: number, comparison: number) {
    return to === comparison;
  }

  isFirstNumber(lessThan: number, comparison: number) {
    return lessThan < comparison;
  }

  createBreadcrumb([{ name, routerLink }]): BreadcrumbModel {
    return {
      name,
      routerLink,
    } as BreadcrumbModel;
  }

  removeElementsFromDom(args: HTMLElement[]) {
    args.forEach((element) => {
      if (isPlatformBrowser(this.platformId)) {
        element.hidden = true;
        element.removeAttribute('class');
      }
    });
  }

  append(valueOne: string, valueTwo: string) {
    return (valueOne += valueTwo);
  }

  minus(fromNumberOne: number, numberTwo: number) {
    return fromNumberOne - numberTwo;
  }

  buildBreadcrumbsFromWords(breadcrumbs: BreadcrumbModel) {
    const newBreadcrumbs = [] as BreadcrumbModel[];
    breadcrumbs.name.split('').forEach((crumbName) => {
      newBreadcrumbs.push({
        name: crumbName,
        routerLink: '',
      } as BreadcrumbModel);
    });

    return newBreadcrumbs;
  }

  getIncreasedHeightIndex(crumbs: BreadcrumbModel[], element: HTMLElement, numberOfArrows = 0) {
    const elementCopy = this.createElementWithRequiredWidth(numberOfArrows);

    elementCopy.innerText = '';
    // elementCopy.innerText = '';

    const elementHeight = this.getElementHeight(element);

    let newElementHeight = elementHeight;
    let lastIndex = 0;

    for (const [index, crumb] of crumbs.entries()) {
      if (elementHeight === newElementHeight) {
        elementCopy.append(`${crumb.name} `);
      }

      newElementHeight = this.getElementHeight(elementCopy);

      const isHeightGreater = this.checkIfHeightIsGreater(newElementHeight, elementHeight);
      if (isHeightGreater) {
        lastIndex = index;
        break;
      }

      lastIndex = index;
    }
    return lastIndex;
  }

  isHeightChanged(crumbs: BreadcrumbModel[], element: HTMLElement, numberOfArrows: number) {
    let result = false;

    if (isPlatformBrowser(this.platformId)) {
      const elementCopy = this.createElementWithRequiredWidth(numberOfArrows);
      elementCopy.innerText = '';
      const elementHeight = this.getElementHeight(element);

      result = this.testBreadcrumbsFitElement(crumbs, elementCopy, elementHeight);
    }
    return result;
  }

  testBreadcrumbsFitElement(breadcrumbs: BreadcrumbModel[], element: HTMLElement, elementHeight: number): boolean {
    let result = false;

    let newElementHeight = elementHeight;

    for (const [index, crumb] of breadcrumbs.entries()) {
      const crumbChars = crumb.name.split('');
      for (const crumbChar of crumbChars) {
        if (elementHeight === newElementHeight) {
          element.append(`${crumbChar}`);
        }

        newElementHeight = this.getElementHeight(element);

        const isHeightGreater = this.checkIfHeightIsGreater(newElementHeight, elementHeight);
        if (isHeightGreater) {
          result = true;
          break;
        }
      }

      element.append(' ');
    }

    return result;
  }

  createElementWithRequiredWidth(breadcrumbsLength: number) {
    const elementCopy = this.getFirstElementBy('required-to-do-work-before-init-breadcrumb-template');

    const arrowElement = this.getFirstElementBy('fa-angle-left');
    const arrowWidth = this.getElementWidth(arrowElement);
    const arrowWidthTotal = arrowWidth * breadcrumbsLength;

    const homeElement = this.getFirstElementBy('fa-home');
    const homeIcon = this.getElementWidth(homeElement);

    const breadcrumbWidth = this.getElementWidth(elementCopy);

    const iconsTotal = Number(arrowWidthTotal) + Number(homeIcon);
    const newWidth = Number(breadcrumbWidth) - iconsTotal;
    elementCopy.style.width = newWidth.toString() + 'px';
    return elementCopy;
  }

  checkIfHeightIsGreater(newElementHeight: number, elementHeight: number) {
    return newElementHeight > elementHeight;
  }

  getElementHeight(element: HTMLElement) {
    if (isPlatformBrowser(this.platformId)) {
      return element.offsetHeight;
    }
  }

  getElementWidth(element: HTMLElement) {
    if (isPlatformBrowser(this.platformId)) {
      return element.offsetWidth;
    }
  }

  getFirstElementBy(className: string): any {
    if (isPlatformBrowser(this.platformId)) {
      return document.getElementsByClassName(className)[0] as HTMLElement;
    }
  }
}
